import * as XLSX from 'xlsx';
import i18n from '@/i18n';

/**
 * Yagona mahsulot modeli — barcha foydalanuvchilar (do'kon egasi, menejer,
 * platforma adminlari) uchun bir xil 11 maydon.
 */
export const PRODUCT_FIELDS = [
  'name',
  'sku',
  'brend_nomi',
  'atribut_nomi',
  'mhik',
  'mxik_nomi',
  'barcode',
  'category',
  'unit',
  'sale_price',
  'is_murabaha_eligible',
  'allowed_tenors',
] as const;

export type ProductFieldKey = typeof PRODUCT_FIELDS[number];

// Getters so labels resolve lazily and follow the current language.
export const PRODUCT_FIELD_LABELS: Record<ProductFieldKey, string> = {
  get name() { return i18n.t('misc_pschema.f_name'); },
  get sku() { return i18n.t('misc_pschema.f_sku'); },
  get brend_nomi() { return i18n.t('misc_pschema.f_brend_nomi'); },
  get atribut_nomi() { return i18n.t('misc_pschema.f_atribut_nomi'); },
  get mhik() { return i18n.t('misc_pschema.f_mhik'); },
  get mxik_nomi() { return i18n.t('misc_pschema.f_mxik_nomi'); },
  get barcode() { return i18n.t('misc_pschema.f_barcode'); },
  get category() { return i18n.t('misc_pschema.f_category'); },
  get unit() { return i18n.t('misc_pschema.f_unit'); },
  get sale_price() { return i18n.t('misc_pschema.f_sale_price'); },
  get is_murabaha_eligible() { return i18n.t('misc_pschema.f_is_murabaha_eligible'); },
  get allowed_tenors() { return i18n.t('misc_pschema.f_allowed_tenors'); },
};

/** Murabaha uchun ruxsat etilgan bo'lib to'lash muddatlari (oy) */
export const PRODUCT_TENORS = [3, 6, 12, 18, 24] as const;

/** "3,6,12" yoki massivni normallashtirilgan muddatlar massiviga o'girish */
export function parseTenors(value: unknown): number[] {
  if (value === null || value === undefined || value === '') return [];
  const raw = Array.isArray(value) ? value : String(value).split(/[,;\s]+/);
  const nums = raw
    .map(v => parseInt(String(v), 10))
    .filter(n => (PRODUCT_TENORS as readonly number[]).includes(n));
  return [...new Set(nums)].sort((a, b) => a - b);
}

export const formatTenors = (tenors: number[] | null | undefined) =>
  tenors && tenors.length > 0 ? `${tenors.join(', ')} ${i18n.t('misc_pschema.months')}` : '—';

export const PRODUCT_UNITS = ['dona', 'komplekt', 'kg', 'litr', 'metr', 'pachka'];

export const emptyProductForm = {
  name: '',
  sku: '',
  brend_nomi: '',
  atribut_nomi: '',
  mhik: '',
  mxik_nomi: '',
  barcode: '',
  category: '',
  unit: 'dona',
  sale_price: '',
  price: '',
  photo_url: '',
  is_murabaha_eligible: false,
  allowed_tenors: [...PRODUCT_TENORS] as number[],
};

export type ProductFormState = typeof emptyProductForm;

/** Formada majburiy maydonlar (is_murabaha_eligible — boolean, tekshirilmaydi) */
const REQUIRED_TEXT_FIELDS: ProductFieldKey[] = [
  'name', 'sku', 'brend_nomi', 'atribut_nomi', 'mhik', 'mxik_nomi', 'barcode', 'category', 'unit',
];

/** Forma validatsiyasi — bo'sh majburiy maydonlar ro'yxatini qaytaradi */
export function validateProductForm(form: ProductFormState): Partial<Record<string, string>> {
  const errors: Partial<Record<string, string>> = {};
  for (const key of REQUIRED_TEXT_FIELDS) {
    const val = String((form as any)[key] ?? '').trim();
    if (!val) errors[key] = i18n.t('misc_pschema.required', { field: PRODUCT_FIELD_LABELS[key] });
  }
  const price = parseFloat(form.sale_price);
  if (!form.sale_price || isNaN(price) || price <= 0) {
    errors.sale_price = i18n.t('misc_pschema.price_required');
  }
  if (form.is_murabaha_eligible && parseTenors(form.allowed_tenors).length === 0) {
    errors.allowed_tenors = i18n.t('misc_pschema.tenor_required');
  }
  return errors;
}

export function isProductFormValid(form: ProductFormState): boolean {
  return Object.keys(validateProductForm(form)).length === 0;
}

/** Mahsulot yozuvidan forma holatini yasash */
export function productToForm(p: any): ProductFormState {
  return {
    name: p.name || '',
    sku: p.sku || '',
    brend_nomi: p.brend_nomi || '',
    atribut_nomi: p.atribut_nomi || '',
    mhik: p.mhik || '',
    mxik_nomi: p.mxik_nomi || '',
    barcode: p.barcode || '',
    category: p.category || '',
    unit: p.unit || 'dona',
    sale_price: p.sale_price ? String(p.sale_price) : '',
    price: p.price ? String(p.price) : '',
    photo_url: p.photo_url || '',
    is_murabaha_eligible: !!p.is_murabaha_eligible,
    allowed_tenors: parseTenors(p.allowed_tenors).length > 0
      ? parseTenors(p.allowed_tenors)
      : [...PRODUCT_TENORS] as number[],
  };
}

/** Formadan DB/edge uchun payload */
export function formToPayload(form: ProductFormState) {
  return {
    name: form.name.trim(),
    sku: form.sku.trim(),
    brend_nomi: form.brend_nomi.trim(),
    atribut_nomi: form.atribut_nomi.trim(),
    mhik: form.mhik.trim(),
    mxik_nomi: form.mxik_nomi.trim(),
    barcode: form.barcode.trim(),
    category: form.category.trim(),
    unit: form.unit,
    sale_price: parseFloat(form.sale_price) || 0,
    is_murabaha_eligible: form.is_murabaha_eligible,
    allowed_tenors: parseTenors(form.allowed_tenors),
  };
}

export interface ImportRow {
  _row: number;
  _valid: boolean;
  _error: string;
  name: string;
  sku: string;
  brend_nomi: string;
  atribut_nomi: string;
  mhik: string;
  mxik_nomi: string;
  barcode: string;
  category: string;
  unit: string;
  sale_price: number;
  is_murabaha_eligible: boolean;
  allowed_tenors: number[];
}

const pick = (row: any, keys: string[]) => {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') return String(row[k]);
  }
  return '';
};

/** Excel qatorlarini yagona modelga o'girish va tekshirish */
export function mapAndValidateImportRows(rows: any[]): ImportRow[] {
  return rows.map((row, idx) => {
    const mapped: ImportRow = {
      _row: idx + 2,
      _valid: true,
      _error: '',
      name: pick(row, ['name', 'Nomi', 'nomi', 'NOMI']).trim(),
      sku: pick(row, ['sku', 'SKU']).trim(),
      brend_nomi: pick(row, ['brend_nomi', 'BREND NOMI', 'Brend', 'Brend nomi']).replace(/^-+$/, '').trim(),
      atribut_nomi: pick(row, ['atribut_nomi', 'ATRIBUT NOMI', 'Atribut', 'Atribut nomi']).replace(/^-+$/, '').trim(),
      mhik: pick(row, ['mhik', 'MHIK', 'MXIK', 'MXIK KODI', 'MXIK kodi']).trim(),
      mxik_nomi: pick(row, ['mxik_nomi', 'MXIK NOMI', 'MXIK nomi', 'MHIK nomi']).trim(),
      barcode: pick(row, ['barcode', 'Shtrix-kod', 'Shtrix kod', 'shtrix_kod']).trim(),
      category: pick(row, ['category', 'Kategoriya', 'kategoriya']).trim(),
      unit: (pick(row, ['unit', 'Birlik', 'birlik']) || 'dona').trim(),
      sale_price: parseFloat(pick(row, ['sale_price', 'Narxi', 'narxi', 'narx'])) || 0,
      is_murabaha_eligible: ['true', '1', 'ha', 'yes'].includes(
        pick(row, ['is_murabaha_eligible', 'Murabaha', 'murabaha']).toLowerCase()
      ),
      allowed_tenors: (() => {
        const parsed = parseTenors(pick(row, ['allowed_tenors', 'Muddatlar', 'muddat', 'Muddat', 'muddatlar']));
        return parsed.length > 0 ? parsed : [...PRODUCT_TENORS] as number[];
      })(),
    };

    const missing: string[] = [];
    for (const key of REQUIRED_TEXT_FIELDS) {
      if (!String((mapped as any)[key] || '').trim()) missing.push(PRODUCT_FIELD_LABELS[key]);
    }
    if (!mapped.sale_price || mapped.sale_price <= 0) missing.push(PRODUCT_FIELD_LABELS.sale_price);
    if (mapped.is_murabaha_eligible && mapped.allowed_tenors.length === 0) missing.push(PRODUCT_FIELD_LABELS.allowed_tenors);

    if (missing.length > 0) {
      mapped._valid = false;
      mapped._error = i18n.t('misc_pschema.empty_prefix', { fields: missing.join(', ') });
    }
    return mapped;
  });
}

/** Excel faylni o'qib, yagona modelga o'giradi */
export function readProductsExcel(file: File): Promise<ImportRow[]> {
  return readProductsExcelWithCsv(file).then(({ rows }) => rows);
}

/**
 * FR-18: server (`POST /products/import`) faqat CSV qabul qiladi va har
 * qatorni mustaqil tekshiradi — bu yerda faqat ko'rib chiqish uchun
 * qatorlar hisoblanadi (mapAndValidateImportRows), yakuniy tekshiruv va
 * yozish serverda bo'ladi. `csv` — asl workbook'dan hosil qilingan matn,
 * xuddi shu qatorlar bilan (import preview'dagi "_valid" filtri bu yerga
 * ta'sir qilmaydi — server o'zi filtrlaydi).
 */
export function readProductsExcelWithCsv(file: File): Promise<{ rows: ImportRow[]; csv: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target?.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(ws);
        if (rows.length === 0) return reject(new Error(i18n.t('misc_pschema.file_empty')));
        const csv = XLSX.utils.sheet_to_csv(ws);
        resolve({ rows: mapAndValidateImportRows(rows), csv });
      } catch {
        reject(new Error(i18n.t('misc_pschema.excel_read_fail')));
      }
    };
    reader.onerror = () => reject(new Error(i18n.t('misc_pschema.file_read_fail')));
    reader.readAsBinaryString(file);
  });
}

/** Yagona mahsulot shabloni (hamma joyda bir xil) */
export function downloadProductTemplate(fileName = 'mahsulot-shablon.xlsx') {
  const template = [
    {
      name: 'Namuna mahsulot',
      sku: 'SKU-001',
      brend_nomi: 'Samsung',
      atribut_nomi: 'Qora, 55 dyum',
      mhik: '04811100100100100',
      mxik_nomi: 'Televizorlar',
      barcode: '8800000000001',
      category: 'Elektronika',
      unit: 'dona',
      sale_price: 1000000,
      is_murabaha_eligible: 'ha',
      allowed_tenors: '3,6,12,18,24',
    },
  ];
  const ws = XLSX.utils.json_to_sheet(template, { header: [...PRODUCT_FIELDS] });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Mahsulotlar');
  XLSX.writeFile(wb, fileName);
}

/** Import qatoridan DB yozuvi */
export function importRowToInsert(r: ImportRow, merchantId: string) {
  return {
    merchant_id: merchantId,
    name: r.name,
    sku: r.sku,
    brend_nomi: r.brend_nomi,
    atribut_nomi: r.atribut_nomi,
    mhik: r.mhik,
    mxik_nomi: r.mxik_nomi,
    barcode: r.barcode,
    category: r.category,
    unit: r.unit,
    sale_price: r.sale_price,
    is_murabaha_eligible: r.is_murabaha_eligible,
    allowed_tenors: r.allowed_tenors,
  };
}
